import type {
  InventoryMovementRecord,
  ProductComponentRecord,
  TimesheetEntryRecord,
} from '../types/records'
import { bomBreakdown, lineCost } from './productionCost'
import { filterBomForDate } from './materialRequirements'

export type CostBreakdown = {
  materials: number
  labor: number
  total: number
}

export type SlotCostBreakdown = {
  plan: CostBreakdown
  actual: CostBreakdown
}

export function plannedSlotCost(
  bom: ProductComponentRecord[],
  plannedQty: number,
  slotDateIso: string,
): CostBreakdown {
  const rows = filterBomForDate(bom, slotDateIso).map((r) => ({
    quantity: (Number(r.quantity) || 0) * (Number(plannedQty) || 0),
    consumable: r.expand?.consumable ?? null,
  }))
  const b = bomBreakdown(rows)
  return { materials: b.materials, labor: b.labor, total: b.total }
}

export function laborCostFromTimesheets(entries: TimesheetEntryRecord[]): number {
  let total = 0
  for (const e of entries) {
    if (!e.present) continue
    const hours = Number(e.hours) || 0
    const rate = Number(e.expand?.worker?.hourlyRate) || 0
    total += lineCost(hours, rate)
  }
  return Math.round(total * 100) / 100
}

export function materialsCostFromMovements(movements: InventoryMovementRecord[]): number {
  let total = 0
  for (const m of movements) {
    if (m.type !== 'issue') continue
    const price = Number(m.expand?.consumable?.price) || 0
    const qty = Math.abs(Number(m.quantity) || 0)
    total += lineCost(qty, price)
  }
  return Math.round(total * 100) / 100
}

export function slotCostBreakdown(params: {
  bom: ProductComponentRecord[]
  slotDateIso: string
  plannedQty: number
  timesheetEntries: TimesheetEntryRecord[]
  inventoryMovements: InventoryMovementRecord[]
}): SlotCostBreakdown {
  const planMaterialsLabor = plannedSlotCost(params.bom, params.plannedQty, params.slotDateIso)
  const laborActual = laborCostFromTimesheets(params.timesheetEntries)
  const materialsActual = materialsCostFromMovements(params.inventoryMovements)

  return {
    plan: planMaterialsLabor,
    actual: {
      materials: materialsActual,
      labor: laborActual,
      total: Math.round((materialsActual + laborActual) * 100) / 100,
    },
  }
}

export function costPerUnit(total: number, quantity: number): number | null {
  const q = Number(quantity) || 0
  if (q <= 0) return null
  return Math.round((total / q) * 100) / 100
}
